import React, { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ZoomableImage } from '../images/ZoomableImage';

interface ImagePopupProps {
  images: string[];
  currentIndex: number;
  title?: string;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onPreviousProduct?: () => void;
  onNextProduct?: () => void;
  onTitleChange?: (newTitle: string) => void;
}

export function ImagePopup({
  images,
  currentIndex,
  title,
  onClose,
  onPrevious,
  onNext,
  onPreviousProduct,
  onNextProduct,
  onTitleChange
}: ImagePopupProps) {
  const [isZoomed, setIsZoomed] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title || '');
  const [lastEnterTime, setLastEnterTime] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLTextAreaElement>(null);

  const currentImage = images[currentIndex] ?? '';

  useEffect(() => {
    setEditedTitle(title || '');
  }, [title]);

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  const handleTitleSave = () => {
    if (onTitleChange && editedTitle !== title) {
      onTitleChange(editedTitle);
    }
    setIsEditingTitle(false);
  };

  const handleTitleClick = () => {
    if (!onTitleChange || isEditingTitle) return;
    setIsEditingTitle(true);
  };

  const handleTitleBlur = () => {
    handleTitleSave();
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setEditedTitle(title || '');
      setIsEditingTitle(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (isZoomed) return;

    switch (e.key) {
      case 'ArrowLeft':
        if (!isEditingTitle && currentIndex > 0) onPrevious();
        break;
      case 'ArrowRight':
        if (!isEditingTitle && currentIndex < images.length - 1) onNext();
        break;
      case 'ArrowUp':
        if (!isEditingTitle && onPreviousProduct) onPreviousProduct();
        break;
      case 'ArrowDown':
        if (!isEditingTitle && onNextProduct) onNextProduct();
        break;
      case 'Escape':
        if (isEditingTitle) {
          setEditedTitle(title || '');
          setIsEditingTitle(false);
        } else {
          onClose();
        }
        break;
      case 'Enter':
        if (!e.shiftKey && !e.ctrlKey && !e.altKey) {
          const now = Date.now();
          if (isEditingTitle) {
            handleTitleSave();
            setLastEnterTime(now);
          } else if (lastEnterTime && now - lastEnterTime < 500) {
            onClose();
          } else {
            setLastEnterTime(now);
          }
        }
        break;
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isZoomed) return;
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isZoomed) return;
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd || isZoomed) return;

    const distance = touchStart - touchEnd;
    if (distance > 50 && currentIndex < images.length - 1) onNext();
    if (distance < -50 && currentIndex > 0) onPrevious();

    setTouchStart(null);
    setTouchEnd(null);
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, isZoomed, onPreviousProduct, onNextProduct, isEditingTitle, lastEnterTime]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4"
        onClick={handleBackdropClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <button
          onClick={e => {
            e.stopPropagation();
            onClose();
          }}
          className="absolute top-4 right-4 text-white hover:text-gray-300 z-50"
        >
          <X className="w-8 h-8" />
        </button>

        {/* Title above image in one card */}
        <div
          ref={containerRef}
          className="relative z-10 flex flex-col w-[min(90vw,calc(90vh-4rem))] max-h-[90vh] bg-neutral-900 rounded-lg overflow-hidden shadow-2xl"
          onClick={e => e.stopPropagation()}
        >
          {title && (
            <div className="flex-shrink-0 px-4 py-3 border-b border-white/10">
              {isEditingTitle ? (
                <textarea
                  ref={titleInputRef}
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  onKeyDown={handleTitleKeyDown}
                  onBlur={handleTitleBlur}
                  className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1 text-base sm:text-lg resize-none"
                  rows={2}
                />
              ) : (
                <div
                  className={`w-full ${onTitleChange ? 'cursor-pointer hover:bg-white/5 rounded px-1 -mx-1' : ''}`}
                  onClick={onTitleChange ? handleTitleClick : undefined}
                >
                  <h2 className="text-white text-base sm:text-lg font-medium text-center leading-snug">
                    {title}
                  </h2>
                </div>
              )}
            </div>
          )}

          {/* Image area — aspect-square gives the img a real height to fill */}
          <div className="relative w-full aspect-square bg-neutral-800">
            {!isZoomed && !isEditingTitle && (
              <>
                {currentIndex > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onPrevious();
                    }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 opacity-75 hover:opacity-100 transition-opacity z-20"
                  >
                    <ChevronLeft className="w-10 h-10 sm:w-12 sm:h-12" />
                  </button>
                )}

                {currentIndex < images.length - 1 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onNext();
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 opacity-75 hover:opacity-100 transition-opacity z-20"
                  >
                    <ChevronRight className="w-10 h-10 sm:w-12 sm:h-12" />
                  </button>
                )}

                {onPreviousProduct && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreviousProduct();
                    }}
                    className="absolute left-1/2 top-2 -translate-x-1/2 text-white hover:text-gray-300 opacity-75 hover:opacity-100 transition-opacity z-20"
                  >
                    <ChevronUp className="w-10 h-10 sm:w-12 sm:h-12" />
                  </button>
                )}

                {onNextProduct && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onNextProduct();
                    }}
                    className="absolute left-1/2 bottom-10 -translate-x-1/2 text-white hover:text-gray-300 opacity-75 hover:opacity-100 transition-opacity z-20"
                  >
                    <ChevronDown className="w-10 h-10 sm:w-12 sm:h-12" />
                  </button>
                )}
              </>
            )}

            {currentImage ? (
              <ZoomableImage
                src={currentImage}
                alt={`商品画像 ${currentIndex + 1}`}
                onZoom={setIsZoomed}
                squareOutput={true}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white/60 text-sm">
                画像がありません
              </div>
            )}

            {images.length > 1 && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white bg-black/50 px-3 py-1 rounded-full z-20 text-sm">
                {currentIndex + 1} / {images.length}
              </div>
            )}
          </div>
        </div>

        <div className="absolute bottom-4 left-4 text-white text-sm space-y-1 z-20 pointer-events-none">
          {!isZoomed && !isEditingTitle && (
            <>
              <div className="bg-black/50 px-2 py-1 rounded hidden md:block">
                <kbd className="px-1 bg-gray-700 rounded">←</kbd>
                <kbd className="px-1 bg-gray-700 rounded ml-1">→</kbd> 画像切替
              </div>
              <div className="bg-black/50 px-2 py-1 rounded hidden md:block">
                <kbd className="px-1 bg-gray-700 rounded">↑</kbd>
                <kbd className="px-1 bg-gray-700 rounded ml-1">↓</kbd> 商品切替
              </div>
              <div className="bg-black/50 px-2 py-1 rounded hidden md:block">
                <kbd className="px-1 bg-gray-700 rounded">ESC</kbd> 閉じる
              </div>
              <div className="bg-black/50 px-2 py-1 rounded hidden md:block">
                <kbd className="px-1 bg-gray-700 rounded">Enter</kbd> 2回連続で閉じる
              </div>
              <div className="bg-black/50 px-2 py-1 rounded md:hidden">
                スワイプで画像切替
              </div>
            </>
          )}
          <div className="bg-black/50 px-2 py-1 rounded">
            クリックまたはホイールで拡大/縮小
          </div>
        </div>

        <div className="fixed bottom-16 left-0 right-0 flex justify-center gap-4 md:hidden z-20">
          <div className="bg-black/70 rounded-lg p-1 flex flex-col gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onPreviousProduct) onPreviousProduct();
              }}
              disabled={!onPreviousProduct}
              className="p-2 text-white hover:bg-gray-800 rounded-md disabled:opacity-50"
            >
              <ChevronUp className="w-6 h-6" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onNextProduct) onNextProduct();
              }}
              disabled={!onNextProduct}
              className="p-2 text-white hover:bg-gray-800 rounded-md disabled:opacity-50"
            >
              <ChevronDown className="w-6 h-6" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
